/**
 * Generic table component for displaying field data with configurable columns.
 * Can be used for both detailed changes (with operation) and aggregated summaries (without operation).
 */

import React from 'react'
import { ConfidenceIndicator } from './confidence-indicator'

export interface FieldTableRowData {
  fieldPath: string
  fieldName: string
  operation?: string
  previousValue: string
  currentValue: string
  confidence?: number
  explanation?: string
}

interface IFieldsTableViewProps {
  rows: FieldTableRowData[]
  showOperation?: boolean
  showConfidenceBar?: boolean
  previousValueLabel?: string
  currentValueLabel?: string
  headerText?: string
  emptyMessage?: string
}

export const FieldsTableView: React.FC<IFieldsTableViewProps> = ({
  rows,
  showOperation = true,
  showConfidenceBar = false,
  previousValueLabel = 'Previous Value',
  currentValueLabel = 'New Value',
  headerText,
  emptyMessage = 'No changes to display.',
}) => {
  if (rows.length === 0) {
    return <div className='c-aiBlockResults-fieldsEmpty'>{emptyMessage}</div>
  }

  return (
    <div className='c-aiBlockResults-changeSetSection'>
      {headerText && <div className='c-aiBlockResults-changeSetHeader'>{headerText}</div>}
      <table className='c-aiBlockResults-fieldsTable'>
        <thead>
          <tr className='c-aiBlockResults-fieldsTable-headerRow'>
            <th className='c-aiBlockResults-fieldsTable-th'>Field</th>
            {showOperation && <th className='c-aiBlockResults-fieldsTable-th'>Operation</th>}
            <th className='c-aiBlockResults-fieldsTable-th'>{previousValueLabel}</th>
            <th className='c-aiBlockResults-fieldsTable-th'>{currentValueLabel}</th>
            <th className='c-aiBlockResults-fieldsTable-th'>Confidence</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.fieldPath || idx} className='c-aiBlockResults-fieldsTable-row'>
              <td className='c-aiBlockResults-fieldsTable-td c-aiBlockResults-fieldsTable-td--field'>
                {row.fieldName}
              </td>
              {showOperation && (
                <td className='c-aiBlockResults-fieldsTable-td c-aiBlockResults-fieldsTable-td--operation'>
                  <span className='c-aiBlockResults-operationTag'>{row.operation}</span>
                </td>
              )}
              <td className='c-aiBlockResults-fieldsTable-td c-aiBlockResults-fieldsTable-td--value c-aiBlockResults-fieldsTable-td--previousValue'>
                {row.previousValue}
              </td>
              <td className='c-aiBlockResults-fieldsTable-td c-aiBlockResults-fieldsTable-td--value c-aiBlockResults-fieldsTable-td--newValue'>
                {row.currentValue}
              </td>
              <td className='c-aiBlockResults-fieldsTable-td c-aiBlockResults-fieldsTable-td--confidence'>
                <ConfidenceIndicator
                  confidence={row.confidence}
                  explanation={row.explanation}
                  showBar={showConfidenceBar}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
